import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import TreeView from '../../common/TreeView';
import { TreeEntities } from '../../../utilities/Enums';
import EditUserPane from './EditUserPane';
import EditCompanyPane from './EditCompanyPane';
import { TreebeardNode } from 'react-treebeard';
import { Role, getRoles } from '../../../api/Role';
import { getTree } from '../../../api/User';
import PropertyPane from './editProperty/PropertyPane';

interface State {
    editId?: number;
    editType?: TreeEntities;
    availableRoles: Role[];
    cursor?: TreebeardNode;
    data: TreebeardNode[];
    isLoading: boolean;
    newEntityCompanyId?: number;
    newEntityPropertyId?: number;
    isAdvanceTab?: boolean;
}

interface Props extends RouteComponentProps<{}> { }

class ManageSubPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            availableRoles: [],
            data: [],
            isLoading: true,
        };

        this.handleClick = this.handleClick.bind(this);
        this.toggleDepthFirst = this.toggleDepthFirst.bind(this);
        this.fetchTreeview = this.fetchTreeview.bind(this);
        this.createUser = this.createUser.bind(this);
        this.handleEntityDelete = this.handleEntityDelete.bind(this);
        this.createProperty = this.createProperty.bind(this);
    }

    componentDidMount() {
        getRoles().then(result => {
            if (result.success) {
                this.setState({
                    availableRoles: result.data!,
                });
            }
        });
    
        const searchParams = new URLSearchParams(this.props.location.search);
        const stateParam = searchParams.get('state');
    
        if (stateParam) {
            const [companyIdStr, propertyIdStr] = stateParam.split('-');
            const companyId = parseInt(companyIdStr, 10);
            const propertyId = parseInt(propertyIdStr, 10);
            this.fetchTreeview(companyId, propertyId);
        } else {
            this.fetchTreeview();
        }
    }
   
    render() {
        let currentSubPage: JSX.Element;
        {
            switch (this.state.editType) {
                case TreeEntities.Company:
                    currentSubPage = (
                        <EditCompanyPane
                            companyId={this.state.editId}
                            createUser={() => this.createUser(undefined, this.state.editId)}
                            createProperty={this.createProperty}
                            updateCompanyId={(newId: number) =>
                                this.setState({
                                    editId: newId,
                                    editType: TreeEntities.Company
                                })
                            }
                            updateTreeview={this.fetchTreeview}
                        />
                    );
                    break;
                case TreeEntities.Property:
                    currentSubPage = (
                        <PropertyPane
                            propertyId={this.state.editId!}
                            updateTreeview={this.fetchTreeview}
                            updatePropertyId={(newId: number) =>
                                this.setState({
                                    editId: newId,
                                    editType: TreeEntities.Property
                                })
                            }
                            companyCreatedFrom={this.state.newEntityCompanyId}
                            createUser={(companyId: number) =>
                                this.createUser(this.state.editId!, companyId)
                            }
                            onPropertyDelete={this.handleEntityDelete}
                            isAdvanceTab={this.state.isAdvanceTab}
                        />
                    );
                    break;
                case TreeEntities.User:
                    currentSubPage = (
                        <EditUserPane
                            userId={this.state.editId!}
                            currentUserAvailableRoles={this.state.availableRoles}
                            updateTreeview={this.fetchTreeview}
                            updateUserId={(newId: number) =>
                                this.setState({ editId: newId, editType: TreeEntities.User })
                            }
                            companyCreatedFrom={this.state.newEntityCompanyId}
                            propertyCreatedFrom={this.state.newEntityPropertyId}
                            onUserDelete={this.handleEntityDelete}
                        />
                    );
                    break;
                case TreeEntities.CancelledCompany:
                    currentSubPage = <div>Select an entity to view.</div>;
                    break;
                case undefined:
                default:
                    currentSubPage = <div>Select an entity to view.</div>;
            }
        }

        return (
            <div className="row">
                <div className="col-3">
                    <TreeView
                        onClick={this.handleClick}
                        cursor={this.state.cursor}
                        data={this.state.data}
                        isLoading={this.state.isLoading}
                        createCompany={() => {
                            this.setState({
                                editId: undefined,
                                editType: TreeEntities.Company
                            });
                        }}
                    />
                </div>
                <div className="col-9">{currentSubPage}</div>
            </div>
        );
    }

    handleClick(node: TreebeardNode, toggled: boolean) {
        // deselect old node
        if (this.state.cursor) {
            this.state.cursor.active = false;
        }
        this.setState(prevState => ({
            editId: node ? node.nodeId : node,
            editType: node ? node.nodeType : node,
            cursor: node
        }));
        // select the new node and toggle its children
        node.active = true;
        node.toggled = toggled;
    }

    fetchTreeview(companyId?: number, propertyId?: number) {
        this.setState({
            isLoading: true
        });
        getTree().then(result => {
            if (result.success && result.data) {
                this.setState({
                    data: this.toggleDepthFirst(result.data),
                    isLoading: false
                }, () => {
                    console.log(this.props.location);
                    if (companyId && propertyId) {
                    const companyNode = this.state.data.find(node => node.nodeId === companyId);
                    if(companyNode) {
                        const propertyNode = companyNode.children!.find(node => node.nodeId === propertyId);
                        this.handleClick(companyNode, true);
                        if (propertyNode) {
                            this.handleClick(propertyNode, false);
                            this.setState({
                                isAdvanceTab: true
                            });
                        }
                    }
                }
                });
            }
        });
    }

    toggleDepthFirst(nodes: TreebeardNode[]): TreebeardNode[] {
        if (nodes.length > 0) {
            nodes.splice(0, 1, {
                ...nodes[0],
                toggled: true,
                children:
                    nodes[0].children && nodes[0].children!.length > 0
                        ? this.toggleDepthFirst(nodes[0].children!)
                        : nodes[0].children
            });
        }
        return nodes;
    }

    createUser(propertyId?: number, companyId?: number) {
        this.setState({
            editId: undefined,
            editType: TreeEntities.User,
            newEntityCompanyId: companyId,
            newEntityPropertyId: propertyId
        });
    }

    handleEntityDelete() {
        this.setState({
            editId: undefined,
            editType: undefined
        });
        this.fetchTreeview();
    }

    createProperty(companyId: number) {
        this.setState({
            editId: undefined,
            editType: TreeEntities.Property,
            newEntityCompanyId: companyId
        });
    }
}

export default withRouter<Props>(ManageSubPage);



// WEBPACK FOOTER //
// ./src/components/pages/admin/ManageSubPage.tsx